import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Snackbar, Alert,
    Tab, IconButton,
    Tabs,
    Typography,
    Stack,
    debounce,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    // --- Start: Added Imports for Alert Form ---
    TextField,
    RadioGroup,
    Radio,
    Select,
    MenuItem,
    FormLabel,
    Collapse,
    Autocomplete,
    CircularProgress,
    Checkbox,
    // --- Added Dialog imports for delete confirmation ---
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    Divider, // Added Divider
    Accordion, // Added for new UI
    AccordionSummary, // Added for new UI
    AccordionDetails, // Added for new UI
    // --- End: Added Imports for Alert Form ---
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove'; // Added for form toggle
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'; // Added for form
import CheckBoxIcon from '@mui/icons-material/CheckBox'; // Added for form
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // Added for new UI
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import info from '../../assets/info-icon.svg';
import infoDim from '../../assets/info-icon-dim.svg'

// --- End: Added Imports ---
import {
    getConfigDetails,
    getAlertKpiProgramsSectors,
    saveAlertConfig,
    updateAlertConfig, createDataPipelineProgram,
    getProgramSectorList,
    getProgramById,
    getProjectListByProgram,
    exportProjectListByProgram,
    getProgramList,
    editDataPipelineProgram,
    addOrUpdateAlertSlaProject,
    createAlertConfig,
    getAlertSlaProjectList,
    getAlertConfigsByProgram,
    updateAlertConfigs,
    deleteAlertConfigById
} from 'service/service';
import Loader from 'components/Loader/loader';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import '../AlertKpi/alert-kpi.scss';
import SuccessConfirmationDialog from 'components/AlertKpi/SuccessModal';
import ErrorDialog from 'components/AlertKpi/ErrorModal';
import { SourceTab } from 'components/AlertKpi/SourceTab';
import { SilencesTab } from 'components/AlertKpi/SilencesTab';
import GroupingAlertTab from 'components/AlertKpi/GroupingAlertTab';
import { VariablesTab } from 'components/AlertKpi/VariablesTab';
import { ChannelsTab } from 'components/AlertKpi/ChannelsTab';
import { ProgramTab } from './ProgramTab';
import { ProjectTab } from './ProjectTab';
import { JobsPipelinesTab } from './JobsPipelinesTab';
import { SLATab } from './SLATab';
import { JobLevelAlertsTab } from './JobLevelAlertsTab';
import moment from 'moment';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { checkRole } from 'utils/roleUtils';
import StatusChip from 'components/Table/StatusChip';
import RowTypography from 'components/Table/RowTypography';
import { ReactComponent as EditIcon } from '../../assets/PepWise2.0/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/PepWise2.0/delete-img-new.svg';
import eventEmitter from 'SnackBarEventEmitter';
import colors from 'assets/theme/base/colors';
import { buildElasticsearchSourceFields } from 'utils/alertPayloadTransformers';
import PageWrapper from 'components/Common/PageWraper';
import { useTheme } from "@mui/material/styles"; // Added useTheme

// --- Added RichTextEditor import from reference ---
import RichTextEditor from 'components/DataEstate/RichTextEditor';
// --- Added AlertErrorDialog import from reference ---
import AlertErrorDialog from './AlertErrorDialog';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';


export interface ProgramVariableRow {
    isEnabled: boolean;
    type: string;
    name: string;
    value: string;
    identifier: string;
    uuid: string;
}

interface projectData {
    id?: number | null;
    projects: string;
    alertType: string;
    channel: string;
    recipients: string[];
    more?: number;
    isEditing: boolean;
    Alert_ID?: string;
    HopName?: string;
    templateName?: string;
    payload?: any;
    serviceNowUrl?: string;
    emailBody?: string;
    emailSubject?: string; // Added for edit
    title?: string;
    body?: string;
    teamsMail?: string;
    allRecipients?: string; // Added for edit
    emailConfig?: any;
    serviceNowConfig?: any;
    teamsConfig?: any;
}

// ===================================================================
// START: TYPESCRIPT FIX
// Define a strong type for the alert form data
// ===================================================================
type AlertType =
    | 'SLA Missed'
    | 'SLA Met'
    | 'SLA Missed, SLA Met'
    | 'ADF Pipeline Failures'
    | 'ADB Workflow Failures'
    | 'Data Asset Failures';

interface AlertData {
    hopName: string;
    alertType: string;
    channel: string;
    templateName: string;
    emailBody: string;
    recipients: string;
    deliveryChannel: string;
    projects: string[];
    emailSubject: string;
    payload: {
        description: string;
        ConfigurationItem: string;
        resource: string;
        metric_name: string;
        AssignmentGroup: string;
        severity: string;
        source: string;
        type: string;
    };
    serviceNowUrl: string;
    title: string;
    body: string;
    teamsMail: string;
}
// ===================================================================
// END: TYPESCRIPT FIX
// ===================================================================

// Icons for multi-select checkboxes - custom white icons for blue background
const icon = (
    <Box
        sx={{
            width: 20,
            height: 20,
            border: '2px solid white',
            borderRadius: '2px',
            backgroundColor: 'transparent'
        }}
    />
);
const checkedIcon = (
    <Box
        sx={{
            width: 20,
            height: 20,
            border: '2px solid white',
            borderRadius: '2px',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: '6px',
                top: '2px',
                width: '5px',
                height: '10px',
                border: 'solid rgb(0, 55, 113)', // Checkmark matches background
                borderWidth: '0 2px 2px 0',
                transform: 'rotate(45deg)'
            }
        }}
    />
);

const ALL_PROJECTS_VALUE = "__ALL_PROJECTS__";
const ALL_PROJECTS_LABEL = "All Projects";

// --- Common Switch Styles ---
const commonSwitchStyles = {
    width: 44,
    height: 22,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: '4px',
        '&.Mui-checked': {
            transform: 'translateX(22px)',
            '& + .MuiSwitch-track': { backgroundColor: '#009BD8', opacity: 1, border: 'none' },
            '& .MuiSwitch-thumb': { backgroundColor: '#EFF6FF' },
        },
        '&.Mui-disabled': {
            '& + .MuiSwitch-track': { backgroundColor: '#71758F', opacity: 0.6, border: '1px solid #71758F' },
            '& .MuiSwitch-thumb': { backgroundColor: '#EFF6FF' },
        }
    },
    '& .MuiSwitch-thumb': { width: 14, height: 14, backgroundColor: '#EFF6FF', boxShadow: 'none' },
    '& .MuiSwitch-track': { borderRadius: 14, backgroundColor: '#71758F', border: '1px solid #71758F', boxSizing: 'border-box', opacity: 1 },
};

// ===================================================================
// START: STATE PRESERVATION FIX
// Update AlertsTab to accept state as props
// ===================================================================
export const AlertsTab = ({
    sourceData, setSourceData, programOptions, sectorOptions,
    projectData, programName, programID,
    // --- Props for state preservation ---
    showAddForm, setShowAddForm,
    newAlertData, setNewAlertData,
    editingAlertId, setEditingAlertId,
    initialAlertData,
    defaultBodyText
}: {
    sourceData: any, setSourceData: any, programOptions: any, sectorOptions: any,
    projectData: any,
    // ===================================================================
    // START: TYPESCRIPT FIX
    // Allow programName and programID to be null
    // ===================================================================
    programName: string | null,
    programID: string | null,
    // ===================================================================
    // END: TYPESCRIPT FIX
    // ===================================================================

    // --- Prop types for state preservation ---
    showAddForm: boolean, setShowAddForm: React.Dispatch<React.SetStateAction<boolean>>,
    newAlertData: AlertData, setNewAlertData: React.Dispatch<React.SetStateAction<AlertData>>,
    editingAlertId: string | number | null, setEditingAlertId: React.Dispatch<React.SetStateAction<string | number | null>>,
    initialAlertData: AlertData,
    defaultBodyText: string
}) => {
// ===================================================================
// END: STATE PRESERVATION FIX
// ===================================================================
    const theme = useTheme(); // Added theme
    const [programLevelAlerts, setProgramLevelAlerts] = useState(false);
    const [projectLevelAlerts, setProjectLevelAlerts] = useState(true);


    // --- START: Added State for Alert Form (from reference) ---
    // ===================================================================
    // START: STATE PRESERVATION FIX
    // Remove local state, as it's now passed in via props
    // ===================================================================
    // const [showAddForm, setShowAddForm] = useState(false); // REMOVED
    // const [editingAlertId, setEditingAlertId] = useState<string | number | null>(null); // REMOVED
    const [isLoading, setIsLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    // ===================================================================
    // END: STATE PRESERVATION FIX
    // ===================================================================

    // Error state for user feedback
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
    // Delete confirmation dialog state
    const [deleteDialog, setDeleteDialog] = useState({ open: false, alert: null as any });

    const showError = (message: string) => {
        setErrorDialog({ open: true, message });
    };
    const [tableData, setTableData] = useState<projectData[]>([]);

    const [expandedAlerts, setExpandedAlerts] = useState<Record<string, boolean>>({});

    const toggleAlertExpand = (alertId: string) => {
        setExpandedAlerts(prev => ({
            ...prev,
            [alertId]: !prev[alertId]
        }));
    };

    const renderProjectsChips = (projectsStr: string) => {
        if (!projectsStr) return null;
        const projList = projectsStr.split(',').map(p => p.trim()).filter(Boolean);
        return (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
                {projList.map((proj) => (
                    <Chip
                        key={proj}
                        label={
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <span>{proj === ALL_PROJECTS_VALUE ? ALL_PROJECTS_LABEL : proj}</span>
                                <span style={{ fontSize: '10px', opacity: 0.6, cursor: 'default' }}>×</span>
                            </Box>
                        }
                        size="small"
                        sx={{
                            backgroundColor: '#E1EBF5',
                            color: '#003771',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            borderRadius: '16px',
                            height: '24px',
                            px: 0.5
                        }}
                    />
                ))}
            </Box>
        );
    };

    const renderRecipientChips = (channel: string, recipients: string[], moreCount?: number, allRecipientsStr?: string) => {
        if (channel === 'Email') {
            return (
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.5}>
                    {recipients.map((rec) => {
                        const displayEmail = rec.length > 20 ? rec.substring(0, 17) + '...' : rec;
                        return (
                            <Chip
                                key={rec}
                                label={displayEmail}
                                size="small"
                                sx={{
                                    backgroundColor: '#E1EBF5',
                                    color: '#003771',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    borderRadius: '16px',
                                    height: '24px'
                                }}
                            />
                        );
                    })}
                    {moreCount && moreCount > 0 ? (
                        <Typography sx={{ fontSize: '0.75rem', color: '#00AEEF', fontWeight: 'bold', ml: 0.5 }}>
                            +{moreCount} more...
                        </Typography>
                    ) : null}
                </Box>
            );
        } else if (channel === 'Service Now') {
            return (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                        variant='tableRowFont'
                        sx={{
                            color: '#009BD8',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            wordBreak: 'break-all'
                        }}
                        onClick={() => allRecipientsStr && window.open(allRecipientsStr, '_blank')}
                    >
                        {allRecipientsStr}
                    </Typography>
                    <OpenInNewIcon sx={{ fontSize: '14px', color: '#009BD8' }} />
                </Box>
            );
        } else {
            const displayTeams = allRecipientsStr && allRecipientsStr.length > 20 ? allRecipientsStr.substring(0, 17) + '...' : allRecipientsStr;
            return (
                <Chip
                    label={displayTeams}
                    size="small"
                    sx={{
                        backgroundColor: '#E1EBF5',
                        color: '#003771',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: '16px',
                        height: '24px'
                    }}
                />
            );
        }
    };

    const handleProgramToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setProgramLevelAlerts(event.target.checked);
        if (event.target.checked) {
            setProjectLevelAlerts(false);
        }
    };

    useEffect(() => {
        // We will now fetch the alerts on component load to populate from our mock DB
        fetchAlerts();
    }, [programName]); // Re-fetch if programName changes

    const handleProjectToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setProjectLevelAlerts(event.target.checked);
        if (event.target.checked) {
            setProgramLevelAlerts(false);
        }
    };


    // ===================================================================
    // START: STATE PRESERVATION FIX
    // Update handlers to use props
    // ===================================================================
    const handleAddRowClick = () => {
        if (showAddForm && !editingAlertId) {
            handleCancelAdd();
        } else {
            handleCancelAdd(); // Reset form
            setNewAlertData({
                ...initialAlertData, // Use prop
                emailBody: defaultBodyText, // Use prop
            });
            setShowAddForm(true); // Open the collapsible area
        }
    };

    const handleCancelAdd = () => {
        setShowAddForm(false); // Use prop setter
        setEditingAlertId(null); // Use prop setter
        setSaveError(null);
        setNewAlertData(initialAlertData); // Use prop setter and prop initialData
    };

    const handleEditAlert = (alert: any) => {
        console.log("Editing alert:", alert);

        // Parse projects - handle both string and array formats
        let projectsArray = [];
        if (alert.projects) {
            if (Array.isArray(alert.projects)) {
                projectsArray = alert.projects;
            } else if (typeof alert.projects === 'string') {
                // Split by comma and clean up whitespace
                projectsArray = alert.projects.split(',').map((p: string) => p.trim()).filter(Boolean);
            }
        }
        // Convert the saved “All Projects” label back to the selector’s ALL_PROJECTS_VALUE so it is selected correctly while editing.
        if (projectsArray.length === 1 && projectsArray[0] === ALL_PROJECTS_LABEL) {
            projectsArray = [ALL_PROJECTS_VALUE];
        }

        // Parse alertType - handle both single and combined alert types
        let alertTypeValue = alert.alertType || '';
        if (alertTypeValue && typeof alertTypeValue === 'string') {
            // Check if it matches the combined option format
            const trimmedType = alertTypeValue.trim();
            if (trimmedType.includes('SLA Missed') && trimmedType.includes('SLA Met')) {
                alertTypeValue = 'SLA Missed, SLA Met';
            } else if (trimmedType.includes('SLA Missed')) {
                alertTypeValue = 'SLA Missed';
            } else if (trimmedType.includes('SLA Met')) {
                alertTypeValue = 'SLA Met';
            } else if (trimmedType === 'Pipeline Failures') {
                // Backward compatibility: alerts saved before the "ADF Pipeline Failures" rename
                alertTypeValue = 'ADF Pipeline Failures';
            }
        }

        // Populate form with existing alert data
        const editData = {
            ...initialAlertData, // Use prop
            alertType: alertTypeValue,
            channel: alert.channel || 'Email',
            templateName: alert.templateName || '',
            projects: projectsArray,

            // Email fields
            emailSubject: alert.emailSubject || '',
            emailBody: alert.emailBody || '',
            // Use the `allRecipients` field which stores the full string
            recipients: (alert.channel === 'Email') ? (alert.allRecipients || '') : '',

            // SNOW fields
            serviceNowUrl: alert.serviceNowUrl || '',
            payload: alert.payload || initialAlertData.payload, // Use prop for initial payload

            // Teams fields
            teamsMail: alert.teamsMail || '',
            title: alert.title || '',
            body: alert.body || ''
        };

        setNewAlertData(editData);
        setEditingAlertId(alert.Alert_ID);  // Use prop setter
        setShowAddForm(true); // Use prop setter
    };
    // ===================================================================
    // END: STATE PRESERVATION FIX
    // ===================================================================

    const handleDeleteAlert = (alert: any) => {
        setDeleteDialog({ open: true, alert });
    };

    const confirmDeleteAlert = async () => {
        const alert = deleteDialog.alert;
        if (!alert) return;

        try {
            setIsLoading(true);

            // Delete from Cosmos DB
            if (alert.Alert_ID) {
                console.log("Deleting alert config from Cosmos DB:", alert.Alert_ID);
                await deleteAlertConfigById(alert.Alert_ID);
            }

            // alert.id is a Cosmos _id, not a SQL row ID. Since these v2 alerts do not have matching SQL rows, skip deleteAlertSlaProject and let the backend handle it without an ID.

            console.log("Alert deleted successfully:", alert.Alert_ID);
            setDeleteDialog({ open: false, alert: null });

            // Refresh the table data from the server
            await fetchAlerts();

        } catch (error: any) {
            showError(`Failed to delete alert: ${error.message}`);
            console.error("Delete error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const cancelDelete = () => {
        setDeleteDialog({ open: false, alert: null });
    };

    const handleSaveAlert = async () => {
        const alertType = newAlertData.alertType;
        try {
            setIsLoading(true);
            setSaveError(null);

            if (!alertType) {
                showError("Alert Type is required. Please select an alert type.");
                setIsLoading(false);
                return;
            }

            if (newAlertData.projects.length === 0) {
                 showError("Please select at least one project.");
                setIsLoading(false);
                return;
            }

            // Build alert configuration  
            const alertConfig = buildAlertConfig();
            if (!alertConfig) { // buildAlertConfig will return null on error
                setIsLoading(false);
                return;
            }

            const isEditMode = editingAlertId !== null;
            let alertId = editingAlertId?.toString();

            if (isEditMode && alertId) {
                // This is an update - use the UPDATE API
                console.log("Updating alert config in Cosmos DB:", alertConfig);

                // Add update-specific properties
                const updatePayload = {
                    ...alertConfig,
                    isCreate: false  // Explicitly set this for updates
                };

                const configResponse = await updateAlertConfigs(alertId, updatePayload);
                console.log("Update response:", configResponse);
                console.log("Update payload sent:", updatePayload);
                console.log("Alert ID used:", alertId);

                if (!configResponse.success) { // Check the success flag from the service
                    console.error("Update failed with response:", configResponse);
                    throw new Error(`Failed to update alert configuration in Cosmos DB: ${JSON.stringify(configResponse)}`);
                }

            } else {
                // This is a create - use the CREATE API
                console.log("Creating alert config in Cosmos DB:", alertConfig);

                const configResponse = await createAlertConfig(alertConfig); // This is { success: true, data: { ... } }

                if (!configResponse.success || !configResponse.data.data?._id) { // Check configResponse.success AND .data.data._id
                    throw new Error("Failed to create alert configuration in Cosmos DB");
                }
                alertId = configResponse.data.data._id; // Get ID from .data.data

                console.log("Alert config created with ID:", alertId);
            }

            if (!alertId) {
                throw new Error("Alert ID is missing after create/update operation.");
            }
            console.log("Alert config created with ID:", alertId);

            const getInviteUsersValue = (data: any): string => {
              switch (data.channel) {
                case 'Email':
                  return JSON.stringify(
                    data.recipients
                      .split(',')
                      .map((s: string) => s.trim())
                      .filter(Boolean),
                  );
                case 'MS Teams':
                  return data.teamsMail;
                case 'SNOW':
                  return data.serviceNowUrl;
                default:
                  return '';
              }
            };


            const deliveryPayload: any = {
                Alert_ID: alertId,
                Type: "Program", // This is "Project Level" but under a "Program"
                DeliveryChannel: newAlertData.channel,
                AlertType: newAlertData.alertType,
                TeamChannel: newAlertData.channel,
                InviteUsers: getInviteUsersValue(newAlertData),
                TemplateName: newAlertData.templateName || "Default",
                Project: alertConfig.project, // This is the list of project names
                // Ensure ProgramID is passed if available
                ProjectId: programID || sourceData.id, // Pass the program ID
            };

            // Leave out deliveryPayload.id because tableData only has Cosmos _id, while the SQL table expects a numeric identity Id; sending it causes edit saves to fail with a 500.

            if (newAlertData.channel === "Email") {
                deliveryPayload.EmailSubject = newAlertData.emailSubject;
                deliveryPayload.EmailBody = newAlertData.emailBody;
            } else if (newAlertData.channel === "SNOW") {
                deliveryPayload.Payload = JSON.stringify(newAlertData.payload);
                deliveryPayload.ServiceNowURL = newAlertData.serviceNowUrl;
            } else if (newAlertData.channel === "MS Teams") {
                deliveryPayload.Title = newAlertData.title;
                deliveryPayload.Body = newAlertData.body;
                deliveryPayload.TeamsMail = newAlertData.teamsMail;
            }

            console.log(editingAlertId ? "Updating delivery channel record:" : "Creating delivery channel record:", deliveryPayload);
            // This service call might not be needed if Cosmos is the source of truth
            // but your original code had it, so we keep it.
            await addOrUpdateAlertSlaProject(deliveryPayload);

            console.log(editingAlertId ? "Alert updated successfully" : "Alert created successfully");
            handleCancelAdd();
            await fetchAlerts();

        } catch (error: any) {
            const errorMsg = error?.message || "Failed to create alert. Please try again.";
            showError(errorMsg);
            console.error("Alert creation error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-activate alerts in EDIT mode (when programID exists)
    const shouldAutoActivate = programID ? true : false;

    /**
     * Generates Cosmos DB variable to fetch job IDs for selected projects
     */
    const buildJobIdCosmosVariable = (projectIds: string[]): any => {
        // Use dot notation for MongoDB filters, because nesting pepwise.body.Project_Id makes MongoDB look for an exact match of the entire pepwise object.
        const cosmosValue = {
            // Physical Mongo collection is "project_jobs" (plural) - confirmed via direct inspection
            collection: 'project_jobs',
            query: { 'pepwise.body.Project_Id': { $in: projectIds } },
            field: 'pepwise.body.Job_Id'
        };

        return {
            type: 'cosmos',
            name: 'Job IDs Variable',
            identifier: 'jobIds',
            value: JSON.stringify(cosmosValue),
            uuid: `var-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            isEnabled: true
        };
    };

    /**
     * Generates Cosmos DB variable to fetch data asset names for selected projects
     */
    const buildDataAssetCosmosVariable = (projectIds: string[]): any => {
        // Dot-notation keys, not nested objects - pls check buildJobIdCosmosVariable for why.
        const nestedQuery = {
            'pepwise.body.Project_Id': { $in: projectIds },
            'pepwise.body.Data_Asset_Name': { $ne: '' }
        };

        const cosmosValue = {
            // Physical Mongo collection is "project_jobs" (plural) - pls check buildJobIdCosmosVariable
            collection: 'project_jobs',
            query: nestedQuery,
            field: 'pepwise.body.Data_Asset_Name'
        };

        return {
            type: 'cosmos',
            name: 'Data Asset Names Variable',
            identifier: 'dataAssetNames',
            value: JSON.stringify(cosmosValue),
            uuid: `var-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            isEnabled: true
        };
    };

    /**
     * Builds Elasticsearch query for ADF Pipeline Failures
     */
    const buildPipelineFailureESQuery = (): any => {
        const sourceFields = buildElasticsearchSourceFields([
            'pepwise.body.metric.azure.datafactory.pipeline_id',
            'pepwise.body.metric.azure.datafactory.pipeline_name',
            'pepwise.body.metric.azure.datafactory.execution_details.execution_status',
            'pepwise.body.metric.azure.datafactory.execution_details.end_time',
            'pepwise.body.metric.azure.datafactory.execution_details.start_time',
            'pepwise.body.metric.azure.datafactory.execution_details.error_message',
            'pepwise.body.metric.azure.datafactory.resource_name',
            'pepwise.body.metric.azure.datafactory.trigger_name'
        ]);

        return {
            _source: sourceFields,
            query: {
                bool: {
                    filter: [
                        {
                            term: {
                                'pepwise.body.metric.azure.datafactory.execution_details.execution_status': 'Failed'
                            }
                        },
                        {
                            terms: {
                                'pepwise.body.metric.azure.datafactory.pipeline_id': '{{jobIds}}'
                            }
                        },
                        {
                            range: {
                                'pepwise.body.metric.azure.datafactory.execution_details.end_time': {
                                    gte: 'now-1h',
                                    lte: 'now'
                                }
                            }
                        }
                    ]
                }
            },
            sort: [
                {
                    'labels.label.pepwise.metric.timestamp': {
                        order: 'desc'
                    }
                }
            ]
        };
    };

    // Builds the Elasticsearch query for failed ADB workflow runs (status 4 only).
    const buildADBWorkflowFailureESQuery = (): any => {
        const sourceFields = buildElasticsearchSourceFields([
            'pepwise.body.job_id',
            'pepwise.body.job_name',
            'labels.label.pepwise.metric.status_code',
            'pepwise.body.execution_details.start_time',
            'pepwise.body.execution_details.end_time'
        ]);

        return {
            _source: sourceFields,
            query: {
                bool: {
                    must: [
                        {
                            term: {
                                'labels.label.pepwise.source.type': 'pepwise.metric.source.type.goldADB'
                            }
                        },
                        {
                            terms: {
                                'pepwise.body.job_id': '{{jobIds}}'
                            }
                        },
                        {
                            term: {
                                'labels.label.pepwise.metric.category': 'azure.databricks.job.run.log'
                            }
                        },
                        {
                            term: {
                                'labels.label.pepwise.metric.status_code': 4
                            }
                        }
                    ],
                    must_not: [
                        {
                            exists: {
                                field: 'pepwise.body.run_name'
                            }
                        }
                    ]
                }
            },
            sort: [
                {
                    'labels.label.pepwise.metric.timestamp': {
                        order: 'desc'
                    }
                }
            ]
        };
    };

    /**
     * Builds Elasticsearch query for Data Asset Failures
     */
    const buildDataAssetFailureESQuery = (): any => {
        const sourceFields = buildElasticsearchSourceFields([
            'pepwise.body.metric.azure.datafactory.pipeline_param.data_asset_name',
            'pepwise.body.metric.azure.datafactory.execution_details.execution_status',
            'pepwise.body.metric.azure.datafactory.execution_details.end_time',
            'pepwise.body.metric.azure.datafactory.execution_details.start_time',
            'pepwise.body.metric.azure.datafactory.pipeline_param.ErrorMessage'
        ]);

        return {
            _source: sourceFields,
            query: {
                bool: {
                    filter: [
                        {
                            term: {
                                'pepwise.body.metric.azure.datafactory.execution_details.execution_status': 'Failed'
                            }
                        },
                        {
                            exists: {
                                field: 'pepwise.body.metric.azure.datafactory.pipeline_param.data_asset_name'
                            }
                        },
                        {
                            terms: {
                                'pepwise.body.metric.azure.datafactory.pipeline_param.data_asset_name': '{{dataAssetNames}}'
                            }
                        },
                        {
                            range: {
                                'pepwise.body.metric.azure.datafactory.execution_details.end_time': {
                                    gte: 'now-1h',
                                    lte: 'now'
                                }
                            }
                        }
                    ]
                }
            },
            sort: [
                {
                    'labels.label.pepwise.metric.timestamp': {
                        order: 'desc'
                    }
                }
            ]
        };
    };

    // Builds Elasticsearch query for SLA Status
    const buildSLAStatusESQuery = (): any => {
        const sourceFields = buildElasticsearchSourceFields([
            'pepwise.body.Project_Id',
            'pepwise.body.Project_Name',
            'pepwise.body.Sla_Status',
            'pepwise.body.Schedule_Date'
        ]);

        return {
            _source: sourceFields,
            query: {
                bool: {
                    filter: [
                        {
                            term: {
                                'labels.label.pepwise.metric.kind.name': 'pepwise.metric.project.sla.status'
                            }
                        },
                        {
                            terms: {
                                'pepwise.body.Project_Id': '{{projectIds}}'
                            }
                        },
                        {
                            terms: {
                                'pepwise.body.Sla_Status': ['SLA Missed', 'SLA Met']
                            }
                        },
                        {
                            range: {
                                'pepwise.body.Schedule_Date': {
                                    gte: 'now/d',
                                    lt: 'now+1d/d'
                                }
                            }
                        }
                    ]
                }
            },
            sort: [
                {
                    '@timestamp': {
                        order: 'desc'
                    }
                }
            ]
        };
    };

    // Builds Cosmos variable for project IDs filtering
    const buildProjectIdCosmosVariable = (projectIds: string[]): any => {
        // Dot-notation key, not a nested object - pls check buildJobIdCosmosVariable for why.
        return {
            type: 'cosmos',
            name: 'Project IDs Variable',
            identifier: 'projectIds',
            value: JSON.stringify({
                // Physical Mongo collection is "project_jobs" (plural) - pls check buildJobIdCosmosVariable
                collection: 'project_jobs',
                query: { 'pepwise.body.Project_Id': { $in: projectIds } },
                field: 'pepwise.body.Project_Id'
            }),
            uuid: `var-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            isEnabled: true
        };
    };

    const buildAlertConfig = () => {
        const alertType = newAlertData.alertType;

        // Route to appropriate builder based on alert type
        // 'Pipeline Failures' is matched for backward compatibility with alerts saved before the rename
        if (alertType === 'ADF Pipeline Failures' || alertType === 'Pipeline Failures') {
            return buildPipelineFailureConfig();
        } else if (alertType === 'ADB Workflow Failures') {
            return buildADBWorkflowFailureConfig();
        } else if (alertType === 'Data Asset Failures') {
            return buildDataAssetFailureConfig();
        } else {
            // Existing SLA alert logic
            return buildSLAAlertConfig();
        }
    };

    /**
     * Builds alert config for SLA monitoring (Elasticsearch-based)
     */
    const buildSLAAlertConfig = () => {
        const isAllProjects = newAlertData.projects.includes(ALL_PROJECTS_VALUE);

        const selectedProjects = isAllProjects
            ? projectData
            : projectData.filter((proj: any) =>
                newAlertData.projects.includes(proj.Project_Name)
            );

        if (selectedProjects.length === 0) {
            showError("No matching projects found. Cannot build SLA Status alert.");
            return null;
        }

        const projectIds = selectedProjects
            .map((proj: any) => proj.Project_Id)
            .filter(Boolean);

        if (projectIds.length === 0) {
            showError("No valid project IDs found. Cannot build SLA Status alert.");
            return null;
        }

        const sectors = Array.from(new Set(selectedProjects
            .map((proj: any) => proj.Sectors)
            .filter(Boolean)))
            .join(',');

        const projectForRuleName = isAllProjects ? 'ALL' : newAlertData.projects.join('_');
        const projectNameList = isAllProjects ? ALL_PROJECTS_LABEL : newAlertData.projects.join(', ');
        const ruleName = `${programName}-${projectForRuleName}-SLA-STATUS`.replace(/\s/g, '_');

        // Build Cosmos variable for project IDs
        const projectIdVariable = buildProjectIdCosmosVariable(projectIds);

        // Build ES query
        const esQuery = buildSLAStatusESQuery();

        // Build base config
        const config: any = {
            ruleName,
            alertStore: true,
            // Keep the selected SLA option instead of replacing it with the generic "SLA Status" label.
            alertType: newAlertData.alertType,
            pepWiseAlertType: "ALERT",
            isNewDataModel: true,
            isActive: shouldAutoActivate,
            sourceConfig: {
                sourceType: "elasticsearch",
                indexName: "logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_prod",
                query: JSON.stringify(esQuery),
                duplicateCheckFields: ['Project_Id', 'Schedule_Date', 'Sla_Status'],
                keysToExtract: ['Project_Id', 'Project_Name', 'Sla_Status', 'Schedule_Date']
            },
            variables: [projectIdVariable],
            sector: sectors,
            project: projectNameList,
            program: programName
        };

        // Add email config if Email channel selected
        if (newAlertData.channel === 'Email') {
            const emailRecipients = newAlertData.recipients
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);

            config.emailConfig = {
                isEnabled: true,
                templateName: newAlertData.templateName || "Default",
                emailSubject: newAlertData.emailSubject || `PEPWISE ${programName} Reporting - SLA Status`,
                htmlTemplate: newAlertData.emailBody || `<p>SLA Status of {{Project_Name}}: {{Sla_Status}}</p>`,
                emailRecipients,
                channel: "Email"
            };
        }

        // Add Service Now config if SNOW channel selected
        if (newAlertData.channel === 'SNOW') {
            config.serviceNowConfig = {
                isEnabled: true,
                templateName: newAlertData.templateName || "Default",
                payload: {
                    data: {
                        description: newAlertData.payload.description || `SLA Status of {{Project_Name}}: {{Sla_Status}}`,
                        ConfigurationItem: newAlertData.payload.ConfigurationItem || "",
                        resource: newAlertData.payload.resource || "",
                        metric_name: newAlertData.payload.metric_name || "",
                        AssignmentGroup: newAlertData.payload.AssignmentGroup || "",
                        severity: newAlertData.payload.severity || "2",
                        source: "PepWise",
                        type: "incident"
                    }
                },
                serviceNowUrl: newAlertData.serviceNowUrl || "",
                channel: "Service Now"
            };
        }

        // Add Teams config if MS Teams channel selected
        if (newAlertData.channel === 'MS Teams') {
            config.teamsConfig = {
                title: newAlertData.title || `PEPWISE ${programName} Reporting - SLA Status`,
                templateName: newAlertData.templateName || "Default",
                bodyTemplate: newAlertData.body || `SLA Status of {{Project_Name}}: {{Sla_Status}}`,
                teamsMail: newAlertData.teamsMail || "",
                isEnabled: true,
                channel: "Teams"
            };
        }

        return config;
    };

    /**
     * Builds alert config for ADF Pipeline Failures (v2 only)
     */
    const buildPipelineFailureConfig = () => {
        const isAllProjects = newAlertData.projects.includes(ALL_PROJECTS_VALUE);

        const selectedProjects = isAllProjects
            ? projectData
            : projectData.filter((proj: any) =>
                newAlertData.projects.includes(proj.Project_Name)
            );

        if (selectedProjects.length === 0) {
            showError("No matching projects found. Cannot build ADF Pipeline Failures alert.");
            return null;
        }

        const projectIds = selectedProjects
            .map((proj: any) => proj.Project_Id)
            .filter(Boolean);

        if (projectIds.length === 0) {
            showError("No valid project IDs found. Cannot build ADF Pipeline Failures alert.");
            return null;
        }

        const sectors = Array.from(new Set(selectedProjects
            .map((proj: any) => proj.Sectors)
            .filter(Boolean)))
            .join(',');

        const projectForRuleName = isAllProjects ? 'ALL' : newAlertData.projects.join('_');
        const projectNameList = isAllProjects ? ALL_PROJECTS_LABEL : newAlertData.projects.join(', ');
        const ruleName = `${programName}-${projectForRuleName}-PIPELINE-FAILURES`.replace(/\s/g, '_');

        // Build Cosmos variable to fetch job IDs
        const jobIdVariable = buildJobIdCosmosVariable(projectIds);

        // Build ES query
        const esQuery = buildPipelineFailureESQuery();

        // Build base config
        const config: any = {
            ruleName,
            alertStore: true,
            alertType: "ADF Pipeline Failures",
            pepWiseAlertType: "ALERT",
            isNewDataModel: true,
            isActive: shouldAutoActivate,
            sourceConfig: {
                sourceType: "elasticsearch",
                indexName: "logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_prod",
                query: JSON.stringify(esQuery),
                duplicateCheckFields: ['pipeline_id', 'execution_status', 'end_time'],
                keysToExtract: ['pipeline_id', 'pipeline_name', 'execution_status', 'end_time', 'start_time', 'error_message', 'resource_name', 'trigger_name']
            },
            variables: [jobIdVariable],
            sector: sectors,
            project: projectNameList,
            program: programName
        };

        // Add channel configs
        if (newAlertData.channel === 'Email') {
            const emailRecipients = newAlertData.recipients
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);

            config.emailConfig = {
                isEnabled: true,
                templateName: newAlertData.templateName || "Default",
                emailSubject: newAlertData.emailSubject || `PEPWISE ${programName} - ADF Pipeline Failures`,
                htmlTemplate: newAlertData.emailBody || `<p>Pipeline {{pipeline_name}} (ID: {{pipeline_id}}) failed at {{end_time}}</p><p>Error: {{error_message}}</p>`,
                emailRecipients,
                channel: "Email"
            };
        }

        if (newAlertData.channel === 'SNOW') {
            config.serviceNowConfig = {
                isEnabled: true,
                templateName: newAlertData.templateName || "Default",
                payload: {
                    data: {
                        description: newAlertData.payload.description || `Pipeline {{pipeline_name}} failed: {{error_message}}`,
                        ConfigurationItem: newAlertData.payload.ConfigurationItem || "",
                        resource: newAlertData.payload.resource || "",
                        metric_name: newAlertData.payload.metric_name || "pipeline_failure",
                        AssignmentGroup: newAlertData.payload.AssignmentGroup || "",
                        severity: newAlertData.payload.severity || "2",
                        source: "PepWise",
                        type: "incident"
                    }
                },
                serviceNowUrl: newAlertData.serviceNowUrl || "",
                channel: "Service Now"
            };
        }

        if (newAlertData.channel === 'MS Teams') {
            config.teamsConfig = {
                title: newAlertData.title || `PEPWISE ${programName} - ADF Pipeline Failures`,
                templateName: newAlertData.templateName || "Default",
                bodyTemplate: newAlertData.body || `Pipeline {{pipeline_name}} (ID: {{pipeline_id}}) failed at {{end_time}}. Error: {{error_message}}`,
                teamsMail: newAlertData.teamsMail || "",
                isEnabled: true,
                channel: "Teams"
            };
        }

        return config;
    };

    /**
     * Builds alert config for ADB Workflow Failures (v2 only)
     */
    const buildADBWorkflowFailureConfig = () => {
        const isAllProjects = newAlertData.projects.includes(ALL_PROJECTS_VALUE);

        const selectedProjects = isAllProjects
            ? projectData
            : projectData.filter((proj: any) =>
                newAlertData.projects.includes(proj.Project_Name)
            );

        if (selectedProjects.length === 0) {
            showError("No matching projects found. Cannot build ADB Workflow Failures alert.");
            return null;
        }

        const projectIds = selectedProjects
            .map((proj: any) => proj.Project_Id)
            .filter(Boolean);

        if (projectIds.length === 0) {
            showError("No valid project IDs found. Cannot build ADB Workflow Failures alert.");
            return null;
        }

        const sectors = Array.from(new Set(selectedProjects
            .map((proj: any) => proj.Sectors)
            .filter(Boolean)))
            .join(',');

        const projectForRuleName = isAllProjects ? 'ALL' : newAlertData.projects.join('_');
        const projectNameList = isAllProjects ? ALL_PROJECTS_LABEL : newAlertData.projects.join(', ');
        const ruleName = `${programName}-${projectForRuleName}-ADB-WORKFLOW-FAILURES`.replace(/\s/g, '_');

        // Build Cosmos variable to fetch job IDs for the selected projects (same lookup used by ADF Pipeline Failures)
        const jobIdVariable = buildJobIdCosmosVariable(projectIds);

        // Build ES query
        const esQuery = buildADBWorkflowFailureESQuery();

        // Build base config
        const config: any = {
            ruleName,
            alertStore: true,
            alertType: "ADB Workflow Failures",
            pepWiseAlertType: "ALERT",
            isNewDataModel: true,
            isActive: shouldAutoActivate,
            sourceConfig: {
                sourceType: "elasticsearch",
                indexName: "logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_prod",
                query: JSON.stringify(esQuery),
                duplicateCheckFields: ['job_id', 'status_code', 'end_time'],
                keysToExtract: ['job_id', 'job_name', 'status_code', 'start_time', 'end_time']
            },
            variables: [jobIdVariable],
            sector: sectors,
            project: projectNameList,
            program: programName
        };

        // Add channel configs
        if (newAlertData.channel === 'Email') {
            const emailRecipients = newAlertData.recipients
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);

            config.emailConfig = {
                isEnabled: true,
                templateName: newAlertData.templateName || "Default",
                emailSubject: newAlertData.emailSubject || `PEPWISE ${programName} - ADB Workflow Failures`,
                htmlTemplate: newAlertData.emailBody || `<p>ADB Workflow {{job_name}} (ID: {{job_id}}) failed at {{end_time}}</p>`,
                emailRecipients,
                channel: "Email"
            };
        }

        if (newAlertData.channel === 'SNOW') {
            config.serviceNowConfig = {
                isEnabled: true,
                templateName: newAlertData.templateName || "Default",
                payload: {
                    data: {
                        description: newAlertData.payload.description || `ADB Workflow {{job_name}} (ID: {{job_id}}) failed`,
                        ConfigurationItem: newAlertData.payload.ConfigurationItem || "",
                        resource: newAlertData.payload.resource || "",
                        metric_name: newAlertData.payload.metric_name || "adb_workflow_failure",
                        AssignmentGroup: newAlertData.payload.AssignmentGroup || "",
                        severity: newAlertData.payload.severity || "2",
                        source: "PepWise",
                        type: "incident"
                    }
                },
                serviceNowUrl: newAlertData.serviceNowUrl || "",
                channel: "Service Now"
            };
        }

        if (newAlertData.channel === 'MS Teams') {
            config.teamsConfig = {
                title: newAlertData.title || `PEPWISE ${programName} - ADB Workflow Failures`,
                templateName: newAlertData.templateName || "Default",
                bodyTemplate: newAlertData.body || `ADB Workflow {{job_name}} (ID: {{job_id}}) failed at {{end_time}}`,
                teamsMail: newAlertData.teamsMail || "",
                isEnabled: true,
                channel: "Teams"
            };
        }

        return config;
    };

    /**
     * Builds alert config for Data Asset Failures (v2 only)
     */
    const buildDataAssetFailureConfig = () => {
        const isAllProjects = newAlertData.projects.includes(ALL_PROJECTS_VALUE);

        const selectedProjects = isAllProjects
            ? projectData
            : projectData.filter((proj: any) =>
                newAlertData.projects.includes(proj.Project_Name)
            );

        if (selectedProjects.length === 0) {
            showError("No matching projects found. Cannot build Data Asset Failures alert.");
            return null;
        }

        const projectIds = selectedProjects
            .map((proj: any) => proj.Project_Id)
            .filter(Boolean);

        if (projectIds.length === 0) {
            showError("No valid project IDs found. Cannot build Data Asset Failures alert.");
            return null;
        }

        const sectors = Array.from(new Set(selectedProjects
            .map((proj: any) => proj.Sectors)
            .filter(Boolean)))
            .join(',');

        const projectForRuleName = isAllProjects ? 'ALL' : newAlertData.projects.join('_');
        const projectNameList = isAllProjects ? ALL_PROJECTS_LABEL : newAlertData.projects.join(', ');
        const ruleName = `${programName}-${projectForRuleName}-DATA-ASSET-FAILURES`.replace(/\s/g, '_');

        // Build Cosmos variable to fetch data asset names
        const dataAssetVariable = buildDataAssetCosmosVariable(projectIds);

        // Build ES query
        const esQuery = buildDataAssetFailureESQuery();

        // Build base config
        const config: any = {
            ruleName,
            alertStore: true,
            alertType: "Data Asset Failures",
            pepWiseAlertType: "ALERT",
            isNewDataModel: true,
            isActive: shouldAutoActivate,
            sourceConfig: {
                sourceType: "elasticsearch",
                indexName: "logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_prod",
                query: JSON.stringify(esQuery),
                duplicateCheckFields: ['data_asset_name', 'execution_status', 'end_time'],
                keysToExtract: ['data_asset_name', 'execution_status', 'end_time', 'start_time', 'ErrorMessage']
            },
            variables: [dataAssetVariable],
            sector: sectors,
            project: projectNameList,
            program: programName
        };

        // Add channel configs
        if (newAlertData.channel === 'Email') {
            const emailRecipients = newAlertData.recipients
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);

            config.emailConfig = {
                isEnabled: true,
                templateName: newAlertData.templateName || "Default",
                emailSubject: newAlertData.emailSubject || `PEPWISE ${programName} - Data Asset Failures`,
                htmlTemplate: newAlertData.emailBody || `<p>Data Asset {{data_asset_name}} failed at {{end_time}}</p><p>Error: {{ErrorMessage}}</p>`,
                emailRecipients,
                channel: "Email"
            };
        }

        if (newAlertData.channel === 'SNOW') {
            config.serviceNowConfig = {
                isEnabled: true,
                templateName: newAlertData.templateName || "Default",
                payload: {
                    data: {
                        description: newAlertData.payload.description || `Data Asset {{data_asset_name}} failed: {{ErrorMessage}}`,
                        ConfigurationItem: newAlertData.payload.ConfigurationItem || "",
                        resource: newAlertData.payload.resource || "",
                        metric_name: newAlertData.payload.metric_name || "data_asset_failure",
                        AssignmentGroup: newAlertData.payload.AssignmentGroup || "",
                        severity: newAlertData.payload.severity || "2",
                        source: "PepWise",
                        type: "incident"
                    }
                },
                serviceNowUrl: newAlertData.serviceNowUrl || "",
                channel: "Service Now"
            };
        }

        if (newAlertData.channel === 'MS Teams') {
            config.teamsConfig = {
                title: newAlertData.title || `PEPWISE ${programName} - Data Asset Failures`,
                templateName: newAlertData.templateName || "Default",
                bodyTemplate: newAlertData.body || `Data Asset {{data_asset_name}} failed at {{end_time}}. Error: {{ErrorMessage}}`,
                teamsMail: newAlertData.teamsMail || "",
                isEnabled: true,
                channel: "Teams"
            };
        }

        return config;
    };

    const fetchAlerts = async () => {
        if (!programName) {
            console.warn("No program name, skipping alert fetch.");
            setTableData([]);
            return;
        }
        setIsLoading(true);
        try {
            const response: any = await getAlertConfigsByProgram(programName);

            console.log("Full response:", response);

            // Try to find the correct nested path for the alerts array
            let alerts = [];
            if (Array.isArray(response?.data?.data)) {
                alerts = response.data.data;
            } else if (Array.isArray(response?.data?.data?.data)) {
                alerts = response.data.data.data;
            } else {
                console.warn("Could not find alerts array in response");
                alerts = [];
            }

            console.log("Final alerts array:", alerts);

            if (response.success && Array.isArray(alerts)) {
                const formattedCosmosAlerts = alerts
                    .filter(config => config.program === programName && config.project !== programName && config.sourceConfig?.scope !== 'job') // Filter for *this* program's project-level alerts (job-level alerts are shown in their own table)
                    .map((config: any) => {
                        let allRecipientsString = "";
                        let channel = "Unknown";
                        let recipientsForTable: string[] = [];
                        let serviceNowUrl = "";
                        let webhookUrl = "";

                        // Determine channel and recipients based on config
                        if (config.emailConfig?.isEnabled) {
                            channel = "Email";
                            recipientsForTable = config.emailConfig.emailRecipients || [];
                            allRecipientsString = recipientsForTable.join(", ");
                        } else if (config.serviceNowConfig?.isEnabled) {
                            channel = "Service Now";
                            serviceNowUrl = config.serviceNowConfig.serviceNowUrl || "";
                            recipientsForTable = serviceNowUrl ? [serviceNowUrl] : [];
                            allRecipientsString = serviceNowUrl;
                        } else if (config.teamsConfig?.isEnabled) {
                            channel = "MS Teams";
                            // Prefer teamsMail over webhookUrl
                            webhookUrl = config.teamsConfig.teamsMail || "";
                            recipientsForTable = webhookUrl ? [webhookUrl] : [];
                            allRecipientsString = webhookUrl;
                        }


                        const maxVisible = 3;
                        const visibleRecipients = recipientsForTable.slice(0, maxVisible);
                        const more =
                            recipientsForTable.length > maxVisible
                                ? recipientsForTable.length - maxVisible
                                : 0;

                        return {
                            id: config._id, // Use _id as the primary id for the row
                            projects: config.project || "",
                            // Backward compatibility: alerts saved before the "ADF Pipeline Failures" rename
                            alertType: config.alertType === 'Pipeline Failures' ? 'ADF Pipeline Failures' : (config.alertType || "SLA Status"),
                            channel: channel,
                            recipients: visibleRecipients, // This is for display in table
                            more,
                            isEditing: false,
                            Alert_ID: config._id, // Store the CosmosDB ID
                            ruleName: config.ruleName,
                            // Store full data for editing
                            emailSubject: config.emailConfig?.emailSubject || "",
                            emailBody: config.emailConfig?.htmlTemplate || "",
                            allRecipients: allRecipientsString, // Store all recipients as string for edit form
                            templateName: config.emailConfig?.templateName || config.serviceNowConfig?.templateName || config.teamsConfig?.templateName || "",
                            serviceNowUrl: serviceNowUrl,
                            // ===================================================================
                            // START: STATE PRESERVATION FIX
                            // Use prop for initial payload
                            // ===================================================================
                            payload: config.serviceNowConfig?.payload?.data || initialAlertData.payload,
                            // ===================================================================
                            // END: STATE PRESERVATION FIX
                            // ===================================================================
                            webhookUrl: webhookUrl,
                            title: config.teamsConfig?.title || "",
                            body: config.teamsConfig?.bodyTemplate || "",
                            emailConfig: config.emailConfig,
                            serviceNowConfig: config.serviceNowConfig,
                            teamsConfig: config.teamsConfig
                        };
                    });

                setTableData(formattedCosmosAlerts);
                console.log("Fetched alerts from Cosmos DB:", formattedCosmosAlerts);
            } else {
                console.warn("No valid alerts found or API failed:", {
                    success: response.success,
                    alertsIsArray: Array.isArray(alerts),
                    alertsLength: alerts?.length || 0
                });
                setTableData([]);
            }
        } catch (error) {
            console.error("Failed to fetch alerts:", error);
            setTableData([]); // Set empty array on error
        } finally {
            setIsLoading(false);
        }
    };


    // --- This function renders the dynamic form (from reference) ---
    // ***** THIS IS THE FULLY CORRECTED FUNCTION *****
    const renderAddAlertForm = () => {
        // Create project options from the prop
        const projectOptions = [
            ALL_PROJECTS_VALUE,
            ...(projectData || []).map((p: any) => p?.projectName || p?.Project_Name).filter(Boolean)
        ];

        // Alert type options - v2 projects get additional failure monitoring types
        const alertTypeOptions = [
            'SLA Missed',
            'SLA Met',
            'SLA Missed, SLA Met',
            ...(['ADF Pipeline Failures', 'ADB Workflow Failures', 'Data Asset Failures'])
        ];

        // --- Custom Blue Theme Input Styles for the form ---
        const blueThemeInputStyles = {
            width: '100%',
            backgroundColor: '#F4F8FC',
            borderRadius: '4px',
            // Label styles
            '& .MuiInputLabel-root': {
                 color: '#5A6A85 !important', // Dark gray/blue for label
                 '&.Mui-focused': { color: '#003771 !important' },
                 '&.MuiInputLabel-shrink': { color: '#003771 !important' },
            },
            // Input text styles
            '& .MuiInputBase-input': { 
                color: '#003771 !important',
                '-webkit-text-fill-color': '#003771 !important'
            },
            // Border styles
            '& .MuiOutlinedInput-root': {
                backgroundColor: '#F4F8FC',
                '& fieldset': { borderColor: '#D2E6F9 !important' },
                '&:hover fieldset': { borderColor: '#009BD8 !important' },
                '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
                '& input': { color: '#003771 !important' }
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D2E6F9 !important',
            },
            // Icon and Select styles
            '& .MuiSvgIcon-root': { color: '#009BD8 !important' },
            '& .MuiSelect-icon': { color: '#009BD8 !important' },
            '& .MuiSelect-select': { color: '#003771 !important' },
            // Chip/Tag styles for Autocomplete dropdown selection
            '& .MuiChip-root': {
                backgroundColor: '#D0E8F9 !important',
                color: '#003771 !important',
                fontWeight: 500,
                borderRadius: '16px !important',
                margin: '2px !important',
                '& .MuiChip-label': {
                    color: '#003771 !important',
                },
                '& .MuiChip-deleteIcon': {
                    color: '#003771 !important',
                    '&:hover': {
                        color: '#001E3E !important',
                    },
                },
            }
        };

        let saveButtonContent;
        if (isLoading) {
            saveButtonContent = <CircularProgress size={20} sx={{ color: 'white' }} />;
        } else if (editingAlertId) {
            saveButtonContent = 'Update';
        } else {
            saveButtonContent = 'Save';
        }

        return (
            <Paper
                sx={{
                    mt: 2,
                    mb: 3,
                    p: '24px !important',
                    backgroundColor: 'transparent !important',
                    color: '#003771 !important',
                    border: '1px solid #D2E6F9 !important',
                    boxShadow: 'none',
                    borderRadius: '8px' 
                }}
            >
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}
                >
                    <Typography
                        variant="subHeader"
                        sx={{
                            color: '#009BD8',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Add Jobs & Alert Types
                    </Typography>
                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px' }} />
                </Box>
                {saveError && (
                    <Typography color="error" sx={{ mt: 1, mb: 1, whiteSpace: 'pre-wrap' }}>
                        Error: {saveError}
                    </Typography>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3, mt: 2.5, mb: 3.5 }}>
                    <Autocomplete
                        multiple
                        sx={{ gridColumn: '1 / span 1' }}
                        options={projectOptions}
                        value={newAlertData.projects}
                        disableCloseOnSelect
                        popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
                        onChange={(event, newValue) => {
                            let updatedValue = [...newValue];
                            const previousProjects = newAlertData.projects;
                            const lastSelected = updatedValue[updatedValue.length - 1];
                            const includesAll = updatedValue.includes(ALL_PROJECTS_VALUE);
                            const previousIncludedAll = previousProjects.includes(ALL_PROJECTS_VALUE);
                            const actualProjectCount = projectOptions.length - 1; // Exclude ALL_PROJECTS_VALUE

                            if (lastSelected === ALL_PROJECTS_VALUE && !previousIncludedAll) {
                                updatedValue = projectOptions; 
                            } else if (newValue.length < previousProjects.length && !includesAll && previousIncludedAll) {
                                updatedValue = [];
                            } else if (newValue.length === actualProjectCount && !includesAll && actualProjectCount > 0) {
                                updatedValue = projectOptions;
                            } else if (includesAll && lastSelected !== ALL_PROJECTS_VALUE && newValue.length < projectOptions.length) {
                                updatedValue = updatedValue.filter(v => v !== ALL_PROJECTS_VALUE);
                            } else {
                                updatedValue = newValue;
                            }
                            setNewAlertData((prev: AlertData) => ({ ...prev, projects: updatedValue }));
                        }}
                        getOptionLabel={(option) =>
                            option === ALL_PROJECTS_VALUE ? ALL_PROJECTS_LABEL : option
                        }
                        renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8, color: '#003771' }}
                                    checked={selected}
                                    sx={{
                                        color: '#003771 !important',
                                        fill: '#003771 !important',
                                        '&.Mui-checked': { color: '#009BD8 !important', fill: '#009BD8 !important' },
                                    }}
                                />
                                {option === ALL_PROJECTS_VALUE ? ALL_PROJECTS_LABEL : option}
                             </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Select Projects*"
                                sx={blueThemeInputStyles} 
                            />
                        )}
                        PaperComponent={({ children }) => (
                            <Paper sx={{
                                backgroundColor: '#FFFFFF !important',
                                color: '#003771 !important',
                                border: '1px solid #D2E6F9',
                                '& .MuiAutocomplete-option': {
                                    color: '#003771 !important',
                                    backgroundColor: '#FFFFFF !important',
                                    '&:hover': { backgroundColor: '#F4F8FC !important' },
                                    '&[aria-selected="true"]': { backgroundColor: '#E1EBF5 !important' }
                                }
                            }}>
                                {children}
                            </Paper>
                        )}
                    />

                    <Select
                        fullWidth
                        value={newAlertData.alertType}
                        onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, alertType: e.target.value }))}
                        displayEmpty
                        IconComponent={ExpandMoreIcon}
                        renderValue={(selected) => {
                            if (!selected) {
                                return <span style={{ color: '#5A6A85' }}>Select Alert*</span>;
                            }
                            return selected;
                        }}
                        sx={blueThemeInputStyles} 
                        inputProps={{ 'aria-label': 'Select Alert' }}
                        MenuProps={{ 
                            PaperProps: {
                                sx: {
                                    backgroundColor: '#FFFFFF !important',
                                    color: '#003771 !important',
                                    border: '1px solid #D2E6F9',
                                    boxShadow: '0px 4px 16px rgba(0, 55, 113, 0.08)',
                                    '& .MuiMenuItem-root': {
                                         color: '#003771 !important',
                                         backgroundColor: '#FFFFFF !important',
                                        '&:hover': { backgroundColor: '#F4F8FC !important' }
                                    },
                                    '& em': { color: '#5A6A85 !important', fontStyle: 'normal' }
                                }
                            }
                        }}
                    >
                        <MenuItem value="" disabled hidden sx={{ display: 'none' }}>
                            <span style={{ color: '#5A6A85' }}>Select Alert*</span>
                        </MenuItem>
                        {alertTypeOptions.map((option) => {
                            const isSelected = newAlertData.alertType === option;
                            return (
                                <MenuItem 
                                    key={option} 
                                    value={option}
                                    sx={{
                                        color: '#003771 !important',
                                        backgroundColor: isSelected ? '#D8F0FA !important' : '#FFFFFF !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        py: 1.2,
                                        px: 2,
                                        fontSize: '13px',
                                        '&:hover': {
                                            backgroundColor: isSelected ? '#D8F0FA !important' : '#F4F8FC !important',
                                        }
                                    }}
                                >
                                    <Typography sx={{ fontSize: '13px', color: '#003771', fontWeight: isSelected ? 500 : 400 }}>
                                        {option}
                                    </Typography>
                                    {isSelected && <CheckIcon sx={{ fontSize: '18px', color: '#003771' }} />}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3.5, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: '#009BD8', fontWeight: 600, whiteSpace: 'nowrap', fontSize: '18px' }}>
                        Configure Delivery Channels
                    </Typography>
                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px !important' }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 3.5 }}>
                    
                    {/* EMAIL PANEL */}
                    <Box sx={{
                        border: 'none',
                        borderRadius: '8px',
                        overflow: 'hidden',
                    }}>
                        {/* Header */}
                        <Box
                            onClick={() => setNewAlertData(prev => ({ ...prev, channel: 'Email' }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1,
                                cursor: 'pointer',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                                <Switch 
                                    checked={newAlertData.channel === 'Email'} 
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setNewAlertData(prev => ({ ...prev, channel: 'Email' }));
                                        }
                                    }} 
                                    size="small"
                                    sx={commonSwitchStyles}
                                />
                                <Typography sx={{ color: '#009BD8 !important', fontWeight: '600 !important', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                                    Email Channel
                                </Typography>
                                <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px', ml: 1, mr: 1 }} />
                            </Box>
                            <IconButton size="small" sx={{ color: '#009BD8' }}>
                                {newAlertData.channel === 'Email' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Collapse in={newAlertData.channel === 'Email'} timeout="auto" unmountOnExit>
                            <Box sx={{ pt: 2, pb: 1, px: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Email Subject*"
                                    variant="outlined"
                                    sx={blueThemeInputStyles}
                                    value={newAlertData.emailSubject || `PEPWISE Reporting - SLA Status`}
                                    onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, emailSubject: e.target.value }))}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Email <body>*"
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    sx={blueThemeInputStyles}
                                    value={newAlertData.emailBody || 'SLA Status of {{Project_Name}}: {{SLA_Status}}'}
                                    onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, emailBody: e.target.value }))}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Select Recipient Emails or DL"
                                    variant="outlined"
                                    sx={blueThemeInputStyles}
                                    value={newAlertData.recipients}
                                    onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, recipients: e.target.value }))}
                                    required
                                />
                            </Box>
                        </Collapse>
                    </Box>

                    {/* SERVICE NOW PANEL */}
                    <Box sx={{
                        borderRadius: '4px',
                        overflow: 'hidden',
                    }}>
                        {/* Header */}
                        <Box
                            onClick={() => setNewAlertData(prev => ({ ...prev, channel: 'Service Now' }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.2,
                                px: 1.5,
                                cursor: 'pointer',
                                backgroundColor: '#EBF3FE',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                                <Switch 
                                    checked={newAlertData.channel === 'Service Now'} 
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setNewAlertData(prev => ({ ...prev, channel: 'Service Now' }));
                                        }
                                    }} 
                                    size="small"
                                    sx={commonSwitchStyles}
                                />
                                <Typography sx={{ color: '#009BD8', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                                    Service Now Channel
                                </Typography>
                                {newAlertData.channel === 'Service Now' && (
                                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', ml: 1, mr: 1 }} />
                                )}
                            </Box>
                            <IconButton size="small" sx={{ color: '#009BD8' }}>
                                {newAlertData.channel === 'Service Now' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Collapse in={newAlertData.channel === 'Service Now'} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 3}}>
                                <TextField fullWidth label="Description" variant="outlined" multiline rows={4} sx={blueThemeInputStyles} value={newAlertData.payload.description} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, payload: { ...prev.payload, description: e.target.value } }))} />
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <TextField fullWidth label="Configuration Item" variant="outlined" sx={blueThemeInputStyles} value={newAlertData.payload.ConfigurationItem} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, payload: { ...prev.payload, ConfigurationItem: e.target.value } }))} />
                                    <TextField fullWidth label="Resource" variant="outlined" sx={blueThemeInputStyles} value={newAlertData.payload.resource} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, payload: { ...prev.payload, resource: e.target.value } }))} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <TextField fullWidth label="Metric Name" variant="outlined" sx={blueThemeInputStyles} value={newAlertData.payload.metric_name} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, payload: { ...prev.payload, metric_name: e.target.value } }))} />
                                    <TextField fullWidth label="Assignment Group" variant="outlined" sx={blueThemeInputStyles} value={newAlertData.payload.AssignmentGroup} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, payload: { ...prev.payload, AssignmentGroup: e.target.value } }))} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <TextField fullWidth label="Severity" variant="outlined" sx={blueThemeInputStyles} value={newAlertData.payload.severity} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, payload: { ...prev.payload, severity: e.target.value } }))} placeholder="2" />
                                    <TextField fullWidth label="Service Now URL" variant="outlined" sx={blueThemeInputStyles} value={newAlertData.serviceNowUrl} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, serviceNowUrl: e.target.value }))} />
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>

                    {/* MS TEAMS PANEL */}
                    <Box sx={{
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        {/* Header */}
                        <Box
                            onClick={() => setNewAlertData(prev => ({ ...prev, channel: 'MS Teams' }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.2,
                                px: 1.5,
                                cursor: 'pointer',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                                <Switch 
                                    checked={newAlertData.channel === 'MS Teams'} 
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setNewAlertData(prev => ({ ...prev, channel: 'MS Teams' }));
                                        }
                                    }} 
                                    size="small"
                                    sx={commonSwitchStyles}
                                />
                                <Typography sx={{ color: '#009BD8', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                                    MS Teams Channel
                                </Typography>
                                {newAlertData.channel === 'MS Teams' && (
                                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', ml: 1, mr: 1 }} />
                                )}
                            </Box>
                            <IconButton size="small" sx={{ color: '#009BD8' }}>
                                {newAlertData.channel === 'MS Teams' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Collapse in={newAlertData.channel === 'MS Teams'} timeout="auto" unmountOnExit>
                            <Box sx={{ pt: 2, pb: 1, px: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField fullWidth placeholder="Title*" variant="outlined" sx={blueThemeInputStyles} value={newAlertData.title || `PEPWISE Reporting - SLA Status`} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, title: e.target.value }))} />
                                <TextField fullWidth placeholder="Teams <body>*" variant="outlined" multiline rows={4} sx={blueThemeInputStyles} value={newAlertData.body} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, body: e.target.value }))} />
                                <TextField fullWidth placeholder="Teams Mail*" variant="outlined" sx={blueThemeInputStyles} value={newAlertData.teamsMail} onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, teamsMail: e.target.value }))} required />
                            </Box>
                        </Collapse>
                    </Box>

                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: 2 }}>
                    <Button 
                        variant="pepwiseButtonText" 
                        onClick={handleCancelAdd} 
                        sx={{ 
                            color: '#00A3E0', 
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            '&:hover': { backgroundColor: 'transparent', color: '#0088BC' }
                        }}
                    >
                        CANCEL
                    </Button>
                    <Button
                        variant="pepwiseSecondary"
                        onClick={handleSaveAlert}
                        sx={{
                            backgroundColor: '#00A3E0',
                            color: 'white !important',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            width: '108px !important',
                            minWidth: '108px !important',
                            maxWidth: '108px !important',
                            height: '36px',
                            p: 0,
                            borderRadius: '5px',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#0088BC', boxShadow: 'none' },
                        }}
                        disabled={isLoading}
                    >
                        {saveButtonContent}
                    </Button>
                </Box>
            </Paper>
        );
    };

    return (
       
        <Box sx={{ mt: 3, p: 3, backgroundColor: '#EFF6FF', color: '#003771', borderRadius: '8px' }}>
            <AlertErrorDialog
                open={errorDialog.open}
                errorMessage={errorDialog.message}
                onClose={() => setErrorDialog({ open: false, message: '' })}
            />
            <Typography variant="body1" sx={{ color: '#5A6A85', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}> {/* UPDATED: Text color */}
              <img src={info} alt=""/>  This section is to configure alerts.
            </Typography>

        
            <Typography variant="subHeader" sx={{ color: '#009BD8', mb: 3, display: 'flex', alignItems: 'center', gap: 1, fontWeight:600 }}>
                Enable Alerts <img src={infoDim} alt="" height={20} width={20}/>
            <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8', marginLeft: 0.5 }} />
            </Typography>
            {/* --- NEW UI: Added Program Level Toggle --- */}
            {/* --- NEW UI: Added Program Level Toggle --- */}
            <Stack spacing={2} sx={{ mb: 3 ,gap:'16px'}}>
                {/* <FormControlLabel
                    control={
                        <Switch
                            checked={programLevelAlerts}
                            onChange={handleProgramToggle}
                            size="small"
                            sx={{
                                width: 44, height: 22, padding: 0,
                                '& .MuiSwitch-switchBase': {
                                    padding: '4px',
                                    '&.Mui-checked': {
                                        transform: 'translateX(22px)',
                                        '& + .MuiSwitch-track': { backgroundColor: '#009BD8', opacity: 1, border: 'none' },
                                        '& .MuiSwitch-thumb': { backgroundColor: '#EFF6FF' },
                                    },
                                },
                                '& .MuiSwitch-thumb': { width: 14, height: 14, backgroundColor: '#009BD8', boxShadow: 'none' },
                                '& .MuiSwitch-track': { borderRadius: 14, backgroundColor: '#EFF6FF', border: '1px solid #009BD8', boxSizing: 'border-box', opacity: 1 },
                            }}
                        />
                    }
                    label={
                        <Typography sx={{ color: '#000000 !important', fontWeight: 600, fontSize: '16px'}}>
                            Configure Program Level Alerts
                        </Typography>
                    }
                />
                 */}
                <FormControlLabel
                    control={
                        <Switch
                            checked={projectLevelAlerts}
                            onChange={handleProjectToggle}
                            size="small"
                            sx={commonSwitchStyles}
                        />
                    }
                    label={
                        <Typography sx={{ color: '#000000 !important', fontWeight: 500, marginLeft: '10px', fontSize: '14px' }}>
                            Configure Project Level Alerts
                        </Typography>
                    }
                />
            </Stack>

            <JobLevelAlertsTab
                programName={programName}
                programID={programID}
                projectData={projectData}
            />

            {projectLevelAlerts && (
                <>
                    {/* ******************************************************************
                    * MODIFICATION 3: Replaced <TableContainer> with <Box> layout
                    * This is the main fix for the "blue table issue"
                    * ****************************************************************** */}
                    <Box mt={2} sx={{ borderRadius: '8px', border: '1px solid #D2E6F9', overflow: 'hidden' }}>
                        {/* --- Table Header --- */}
                        <Box display="flex" sx={{ backgroundColor: '#003771', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color:'white !important' }}>
                            <Typography sx={{ width: '20%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>PROJECTS</Typography>
                            <Typography sx={{ width: '20%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>ALERT TYPE</Typography>
                            <Typography sx={{ width: '15%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>CHANNELS</Typography>
                            <Typography sx={{ width: '35%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>RECIPIENT/URL</Typography>
                            <Typography sx={{ width: '10%', textAlign: 'center', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, fontSize: '0.85rem' }}>ACTIONS</Typography>
                        </Box>

                        {/* --- Table Body --- */}
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, backgroundColor: '#F4F8FC' }}>
                                <CircularProgress sx={{ color: '#003771' }} />
                            </Box>
                        ) : tableData.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, backgroundColor: '#F4F8FC' }}>
                                <Typography sx={{ color: '#003771', fontWeight: 500 }}>
                                    No alerts configured for this program.
                                </Typography>
                            </Box>
                        ) : (
                            tableData.map((row) => {
                                // Extract all active channels for this configuration
                                const activeChannels: any[] = [];
                                if (row.emailConfig?.isEnabled) {
                                    const emailRecipients = row.emailConfig.emailRecipients || [];
                                    const maxVisible = 3;
                                    const moreCount = emailRecipients.length > maxVisible ? emailRecipients.length - maxVisible : 0;
                                    activeChannels.push({
                                        type: 'Email',
                                        recipient: emailRecipients.join(', '),
                                        allRecipients: emailRecipients,
                                        more: moreCount
                                    });
                                }
                                if (row.serviceNowConfig?.isEnabled) {
                                    activeChannels.push({
                                        type: 'Service Now',
                                        recipient: row.serviceNowConfig.serviceNowUrl || '',
                                        isLink: true
                                    });
                                }
                                if (row.teamsConfig?.isEnabled) {
                                    const teamsMail = row.teamsConfig.teamsMail || '';
                                    activeChannels.push({
                                        type: 'MS Teams',
                                        recipient: teamsMail
                                    });
                                }
                                if (activeChannels.length === 0) {
                                    activeChannels.push({
                                        type: row.channel || 'Email',
                                        recipient: row.allRecipients || '',
                                        allRecipients: row.recipients || [],
                                        more: row.more || 0,
                                        isLink: row.channel === 'Service Now'
                                    });
                                }

                                const rowKeyStr = row.id?.toString() || '';
                                const isExpanded = expandedAlerts[rowKeyStr] !== false;
                                const channelsToRender = isExpanded ? activeChannels : [activeChannels[0]];

                                return channelsToRender.map((channelItem, channelIndex) => {
                                    const isFirst = channelIndex === 0;
                                    return (
                                        <Box key={`${row.id}-${channelItem.type}`} display="flex" alignItems="stretch" sx={{
                                            backgroundColor: '#F4F8FC',
                                            borderBottom: '1px solid #D2E6F9',
                                            '&:last-child': {
                                                borderBottom: channelIndex === channelsToRender.length - 1 ? 'none' : '1px solid #D2E6F9'
                                            }
                                        }}>
                                            {/* PROJECTS Column (20%) */}
                                            <Box display="flex" alignItems="center" sx={{ width: '20%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                                {isFirst ? (
                                                    <Box display="flex" alignItems="center" width="100%">
                                                        {activeChannels.length > 1 ? (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleAlertExpand(rowKeyStr)}
                                                                sx={{ color: '#003771', mr: 0.5, p: 0.2 }}
                                                            >
                                                                {isExpanded ? <RemoveIcon sx={{ fontSize: '16px', fontWeight: 'bold' }} /> : <AddIcon sx={{ fontSize: '16px', fontWeight: 'bold' }} />}
                                                            </IconButton>
                                                        ) : (
                                                            <Box sx={{ width: '24px' }} />
                                                        )}
                                                        {renderProjectsChips(row.projects)}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ width: '100%' }} />
                                                )}
                                            </Box>

                                            {/* ALERT TYPE Column (20%) */}
                                            <Box display="flex" alignItems="center" sx={{ width: '20%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                                {isFirst ? (
                                                    <Typography variant="tableRowFont" sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem' }}>
                                                        {row.alertType}
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{ width: '100%' }} />
                                                )}
                                            </Box>

                                            {/* CHANNELS Column (15%) */}
                                            <Box display="flex" alignItems="center" sx={{ width: '15%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                                <Typography variant="tableRowFont" sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem' }}>
                                                    {channelItem.type}
                                                </Typography>
                                            </Box>

                                            {/* RECIPIENT/URL Column (35%) */}
                                            <Box display="flex" alignItems="center" sx={{ width: '35%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                                {channelItem.type === 'Email' ? (
                                                    renderRecipientChips('Email', channelItem.allRecipients || [], channelItem.more)
                                                ) : channelItem.type === 'Service Now' ? (
                                                    renderRecipientChips('Service Now', [], 0, channelItem.recipient)
                                                ) : (
                                                    renderRecipientChips('MS Teams', [], 0, channelItem.recipient)
                                                )}
                                            </Box>

                                            {/* ACTIONS Column (10%) */}
                                            <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: '10%', py: 1.5, px: 2 }}>
                                                {isFirst ? (
                                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: '#009BD8' }}
                                                            onClick={() => handleEditAlert(row)}
                                                            disabled={isLoading}
                                                            data-testid={`edit-alert-${row.id}`}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: '#E53E3E' }}
                                                            onClick={() => handleDeleteAlert(row)}
                                                            disabled={isLoading}
                                                            data-testid={`delete-alert-${row.id}`}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Stack>
                                                ) : (
                                                    <Box sx={{ width: '100%' }} />
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                });
                            })
                        )}
                    </Box>

                    {/* ******************************************************************
                    * MODIFICATION 4: Replaced <Button> with <Typography> for "Add Row"
                    * ****************************************************************** */}
                    <Typography
                        variant="labelText"
                        onClick={handleAddRowClick}
                        sx={{
                            cursor: 'pointer',
                            mt: 2,
                            display: 'flex',
                            alignItems: 'center',
                            ...theme.typography.labelText, // Ensure font styles are applied
                            color: '#009BD8 !important'
                        }}
                    >
                        {showAddForm && !editingAlertId ? <RemoveIcon sx={{ mr: 1 }} /> : <AddIcon sx={{ mr: 1 }} />}
                        {showAddForm && !editingAlertId ? 'Cancel' : (editingAlertId ? 'Cancel Edit' : 'Add Project Level Alert')}
                    </Typography>
                        
                    {/* --- START: Added Collapse block to show form --- */}
                    <Collapse in={showAddForm} timeout="auto" unmountOnExit>
                        {renderAddAlertForm()}
                    </Collapse>
                    {/* --- END: Added Collapse block --- */}
                </>
            )}

            {/* Delete Confirmation Dialog (Already light-themed) */}
            <Dialog
                open={deleteDialog.open}
                onClose={cancelDelete}
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgb(0, 55, 113)', // UPDATED: Dialog theme to match blue
                        color: 'white', // UPDATED: Dialog theme
                        border: '1px solid rgba(255, 255, 255, 0.12)' // UPDATED: Dialog theme
                    }
                }}
            >
                <DialogTitle sx={{ color: 'white' }}> {/* UPDATED: Dialog theme */}
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText color="inherit" sx={{ color: 'white' }}> {/* UPDATED: Dialog theme - color="inherit" prevents MUI's default textSecondary (black) class from overriding sx */}
                        Are you sure you want to delete this alert for "{deleteDialog.alert?.projects}"?
                        <br />
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={cancelDelete}
                        sx={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'none' }} // UPDATED: Dialog theme
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteAlert}
                        sx={{
                            backgroundColor: '#E53E3E',
                            color: 'white',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#D32F2F' }
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};



const ProgramTabDashboard = () => {
    const theme = useTheme(); // Added theme
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    const initialTab = tabParam ? parseInt(tabParam) : 0;
    const [activeTab, setActiveTab] = useState(initialTab);

    const [openSnackbar, setOpenSnackBar] = useState(false);
    const [loading, setLoading] = useState(false);
    const [channels, setChannels] = useState<any>({});
    const { id: configId } = useParams<{ id: string }>();
    const [alertId, setAlertId] = useState(configId || null);
    const [variables, setVariables] = useState<ProgramVariableRow[]>([]);
    const [maintenanceWindows, setMaintenanceWindows] = useState<any>([])
    const [groupingAlertsConfig, setGroupingAlertsConfig] = useState({})
    const [openDialogSuccess, setOpenDialogSuccess] = useState(false)
    const [openErrorDialog, setOpenErrorDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [programOptions, setProgramOptions] = useState([{ title: '', value: '' }]);
    const [sectorOptions, setSectorOptions] = useState([{ title: '', value: '' }]);
    const [snackBar, setSnackbarLoading] = useState(false);

    const [formData, setFormData] = useState<any>({});

    const [startDate, setStartDate] = useState(moment().tz("America/Chicago").startOf('day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().tz("America/Chicago").endOf('day').format('YYYY-MM-DD'));
    const [projectList, setProjectList] = useState<any>([]);
    const [openModal, setOpenModal] = useState<any>({ isOpen: false, message: '' });
    const [programDetail, setProgramDetail] = useState<any>({});
    const { prId, prName } = useParams();
    const [searchProject, setSearchProject] = useState('');
    const [toast, setToast] = useState<any>({
        open: false,
        message: ''
    });
    // const [searchParams] = useSearchParams();
    // const programID = searchParams.get('programID');
    // const programName = searchParams.get('programName');
    const [programCreated, setProgramCreated] = useState(false);
    const [isJobsTabActive, setIsJobsTabActive] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tab = queryParams.get('tab');

        // Enable the flag if tab === '2'
        setIsJobsTabActive(tab === '2');
    }, [location.search]);

    const { pathname, searchParams } = new URL(window.location.href);
    const params = new URLSearchParams(searchParams);
    let programID = params.get('programID');
    let programName = params.get('programName');
    const isEdited = queryParams.get('isEdited') === 'true';

    useEffect(() => {
        if (programName && programID) {
            setFormData((prevData: any) => ({
                ...prevData,
                program: programName,
                id: programID
            }));
        }
    }, [programName, programID]);

    const [controller] = usePepwiseAuthController();
    const { role } = controller;
    const [selectedRows, setSelectedRows] = useState<any>([]);
    const [anchorElDetails, setAnchorElDetails] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorElDetails);
    const [sectorMasterList, setSectorMasterList] = useState<any[]>([]);
    const [sectorList, setSectorList] = useState<any[]>([]);
    const [sectorName, setSectorName] = useState('all');
    const hiddenSector = "SODASTREAM";
    const [programList, setProgramList] = useState<any>([]);
    const [projectData, setProjectData] = useState([]);
    // const updatedSectorOptionsList = sectorList
    // .filter(sector => sector !== hiddenSector)  
    // .map(sector => {
    //  if (sector === "Europe") {
    //  return sector.toUpperCase();  
    //  }
    //  return sector;
    // });
    const filteredSectorList = sectorList.filter(sector => sector !== hiddenSector);
    const { id, name } = useParams();
    const navigate = useNavigate();

    const getProgramData = async (sectorId?: string, searchKeyword?: string) => {
        setLoading(true);
        let data: any = await getProgramList(sectorId, searchKeyword);
        setLoading(false);
        setProgramList(data.data.programData);
    };

    useEffect(() => {
        const activeId = document.getElementById('root');
        if (window.location.pathname.includes('/data-pipeline/programs')) {
            if (activeId) {
                document.head.classList.add('theme-bg-blue');
                document.body.classList.add('theme-bg-blue');
            }
        }
        if (localStorage.getItem('filterSectors') && localStorage.getItem('sectorsMasterData')) {
            setSectorList(JSON.parse(localStorage.getItem('filterSectors') ?? ''));
            setSectorMasterList(JSON.parse(localStorage.getItem('sectorsMasterData') ?? ''));
        } else {
            getSectorData();
        }
    }, []);

    useEffect(() => {
        if (openModal && !openModal.isOpen && openModal.closeType && openModal.closeType == 'updated') {
            let sectorId;
            if (sectorName && sectorName != 'all') {
                sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id
            }
            setSearchProject('');
            getProgramDetails(sectorId);
        }
        if (programName) {
            setFormData((prevFormData: any) => ({
                ...prevFormData,
                program: programName
            }));
        }

    }, [openModal, programName]);

    const getSectorData = async () => {
        let sectors: any = await getProgramSectorList();
        const filterSectors = sectors.data.sectors.map((sec: any) => sec.Sector_Name);
        localStorage.setItem('filterSectors', JSON.stringify(filterSectors));
        localStorage.setItem('sectorsMasterData', JSON.stringify(sectors.data.sectors));
        setSectorList(filterSectors);
        setSectorMasterList(sectors.data.sectors);
    };

    const getProgramDetails = async (sectorId?: string, date?: string) => {
        setLoading(true);
        let result = await getProgramById(programID, sectorId, date || startDate);
        setLoading(false);
        setProgramDetail(result.data?.programData ? result.data.programData[0] : '');
    };

    const handleEditClose = (closeType: any) => {
        setOpenModal({
            ...openModal,
            isOpen: false,
            closeType: closeType
        });
        setSelectedRows([]);
    };

    const getToastData = (data: any) => {
        setToast({
            ...toast,
            message: data.data.message
        });
    };
    const handleTabChange = async (_: React.SyntheticEvent, newValue: number) => {
        // ===================================================================
        // START: STATE PRESERVATION FIX
        // We no longer need to save/update on tab change, just set the active tab.
        // The state is preserved in the parent component.
        // ===================================================================
        // setLoading(true); // NO LONGER NEEDED
        try {
            // if ((newValue > activeTab)) { // NO LONGER NEEDED
            //      if (!isEdited) {
            //          await handleSaveNext();
            //      } else {
            //          await handleUpdateSave();
            //      }
            // }

            // Always allow tab change
            setActiveTab(newValue);
        } finally {
            // setLoading(false); // NO LONGER NEEDED
        }
        // ===================================================================
        // END: STATE PRESERVATION FIX
        // ===================================================================
    };



    const handleClose = () => {
        setOpenSnackBar(false)
    }
    const handleSaveNext = async () => {
        if (programCreated && activeTab === 4) {
            navigate(`/data-pipeline/dataOps`);
        }
        if (programCreated) {
            setActiveTab(activeTab + 1);
            return;
        }
        if (activeTab == 0) {
            if (!isFormValid()) {
                alert("Please fill in all mandatory fields.");
                return;
            } else {
                setSnackbarLoading(true);

                const formPayload = {
                    programName: formData?.program
                };
                let data = {
                    id: programID,
                    // sector: formData.SectorName,
                    programName: programName
                };
                const result = await createDataPipelineProgram(formPayload);

                if (result.data?.message) {
                    if (result.data?.programId)
                        programID = result.data?.programId
                    if (result.data?.programName)
                        programName = result.data?.programName
                    setActiveTab(activeTab + 1)
                    setProgramCreated(true);
                    setOpenSnackBar(true)
                    setSnackbarLoading(false)
                    navigate(`/data-pipeline/programs-tab?programID=${programID}&programName=${programName}`);
                }
            }
        } else if (activeTab >= 1 && activeTab <= 3) {
            setActiveTab(activeTab+1);
        }
        else if (activeTab === 4) { // FIX: Added this block
            navigate(`/data-pipeline/dataOps`);
        }
        getProgramData();
        setOpenModal({
            ...openModal,
            isOpen: true,
            type: 'Project',
            action: 'CREATE'
        });
    };

    const handleCancel = async () => {
        navigate(`/data-pipeline/dataOps`);
    };

    const handleUpdateSave = async () => {
        if (activeTab == 0) {
            if (!isFormValid()) {
                alert("Please fill in all mandatory fields.");
                return;
            }
            const newPrgName = formData.program?.trim();
            const oldPrgName = programName?.trim();

            if (newPrgName === oldPrgName) {
                setActiveTab(activeTab + 1);
                return;
            }

            setSnackbarLoading(true);

            const data = {
                id: formData.id,
                programName: newPrgName
            };

            const result = await editDataPipelineProgram(data);
            if (result.data?.message) {
                setActiveTab(activeTab + 1);
                setOpenSnackBar(true);
                setSnackbarLoading(false);
                navigate(`/data-pipeline/programs-tab?programID=${data.id}&programName=${data.programName}&isEdited=true`);
            }

            setOpenModal({
                ...openModal,
                isOpen: true,
                type: 'Project',
                action: 'EDIT'
            });
        }
        else if (activeTab >= 1 && activeTab <= 3) {
            setActiveTab(activeTab+1); // Go to next tab
        }
        else if (activeTab === 4) { // FIX: Added this block
            navigate(`/data-pipeline/dataOps`);
        }
    };

    const saveConfig = async () => {
        setLoading(true);
        if (alertId) {
            const configData = formData;
            const emailConfig = channels['Email'];
            if (emailConfig) {
                configData.emailConfig = emailConfig;
            } else if (configData.emailConfig) {
                configData.emailConfig = {}
            }
            const serviceNowConfig = channels['Service Now'];
            if (serviceNowConfig) {
                configData.serviceNowConfig = serviceNowConfig;
            } else if (configData.serviceNowConfig) {
                configData.serviceNowConfig = {}
            }
            const teamsConfig = channels['Teams'];
            if (teamsConfig) {
                configData.teamsConfig = teamsConfig;
            } else if (configData.teamsConfig) {
                configData.teamsConfig = {}
            }
            if (!configId) {
                configData.isCreate = true;
            }
            configData.variables = variables
            configData.maintenanceWindows = maintenanceWindows
            configData.groupingAlertsConfig = groupingAlertsConfig
            const result = await updateAlertConfig(alertId, configData);
            setLoading(false);
            return result
        } else {
            const requestBody = {
                program: formData.program,
                project: formData.project,
                sector: formData.sector,
                ruleName: formData.ruleName,
                alertType: formData.alertType,
                sourceConfig: formData.sourceConfig
            }
            const response = await saveAlertConfig(requestBody);
            setLoading(false);
            if (response.data?._id) {
                setAlertId(response.data._id);
            }
            return response
        }
    };

    const isFormValid = () => {
        const mandatoryFieldsValid =
            formData.program && formData.program.trim()
        return mandatoryFieldsValid;
    };

    const fetchData = async () => {
        setLoading(true);
        const result: any = await getAlertKpiProgramsSectors();
        if (result.data) {
            setProgramOptions(result.data.programs)
            setSectorOptions(result.data.sectors)
        }
        setLoading(false);
    };

    const fetchConfig = async () => {
        setLoading(true)
        const response: any = await getConfigDetails(configId, {})
        if (response.data?.alertConfig) {
            const alertConfig = response.data.alertConfig
            setFormData({
                program: alertConfig.program,
                project: alertConfig.project,
                sector: alertConfig.sector,
                ruleName: alertConfig.ruleName,
                alertType: alertConfig.alertType,
                sourceConfig: alertConfig.sourceConfig,
                emailConfig: alertConfig.emailConfig,
                serviceNowConfig: alertConfig.serviceNowConfig,
                teamsConfig: alertConfig.teamsConfig,
                isCreate: false
            })
            setMaintenanceWindows(alertConfig.maintenanceWindows)
            setVariables(alertConfig.variables)
            setGroupingAlertsConfig(alertConfig.groupingAlertsConfig)
            const _channels = {} as any
            if (alertConfig.emailConfig?.emailSubject) {
                _channels['Email'] = alertConfig.emailConfig
            }
            if (alertConfig.serviceNowConfig?.payload) {
                alertConfig.serviceNowConfig.payload = JSON.stringify(alertConfig.serviceNowConfig.payload)
                _channels['Service Now'] = alertConfig.serviceNowConfig
            }
            if (alertConfig.teamsConfig?.teamsMail) {
                 _channels['Teams'] = alertConfig.teamsConfig;
            }
            setChannels(_channels)
        }
    }

    useEffect(() => {
        fetchData();
        if (configId) {
            fetchConfig();
        }
    }, []);

    const trimmedProgram = formData?.program?.trim() || "";
    const isProgramInvalid = !trimmedProgram || trimmedProgram.length > 64;

    // ===================================================================
    // START: STATE PRESERVATION FIX
    // Move Alert form state and initial data definitions here
    // ===================================================================
    const defaultBodyText = `SLA Status of {{Project_Name}}: {{SLA_Status}} for Program: ${programName || ''}`;

    // ===================================================================
    // START: TYPESCRIPT FIX
    // Apply the AlertData type to initialAlertData
    // ===================================================================
    const initialAlertData: AlertData = {
    // ===================================================================
    // END: TYPESCRIPT FIX
    // ===================================================================
        hopName: '',
        alertType: '',
        channel: 'Email',
        templateName: '',
        emailBody: '', // Will be overridden by defaultBodyText on add
        recipients: '',
        deliveryChannel: '',
        projects: [] as string[],

        // Email config
        emailSubject: '',

        // Service Now config
        payload: {
            description: '',
            ConfigurationItem: '',
            resource: '',
            metric_name: '',
            AssignmentGroup: '',
            severity: '2',
            source: 'PepWise',
            type: 'incident'
        },
        serviceNowUrl: '',

        // Teams config
        title: '',
        body: '',
        teamsMail: ''
    };

   
    const [newAlertData, setNewAlertData] = useState<AlertData>(initialAlertData);
    
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingAlertId, setEditingAlertId] = useState<string | number | null>(null);
   

    return loading ? (
        <Loader />
    ) : (
        <PageWrapper sx={{paddingTop:'0px !important'}}>
            <PageHeader title={[{ text: "Programs Dashboard", link: '/data-pipeline/dataOps' },
            { text: isEdited ? 'Edit Program' : 'Create Program' }
            ]}
                icon={DashboardIcon}
            >
                <Typography
                    variant='labelText'
                    style={{
                        color: `${colors.textLight.secondary}`,
                        cursor: 'pointer',
                        marginTop: '6px',
                        marginRight: '25px'
                    }}
                    onClick={handleCancel}
                >
                    CANCEL
                </Typography>
                <Button
                    variant="pepwiseSecondary"
                    onClick={isEdited ? handleUpdateSave : handleSaveNext}
                    disabled={
                        (!isEdited && !isFormValid()) ||
                        (activeTab === 1 && projectData.length === 0) || isProgramInvalid
                    }
                >
                    {isEdited ? "UPDATE & SAVE" : (activeTab === 4 ? "SAVE & CONFIRM" : "SAVE & NEXT")}
                </Button>
                {snackBar && <Loader />}
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={5000}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Box
                        sx={{
                            backgroundColor: '#f0fff4',
                            border: '1px solid #38a169',
                            boxShadow: '0px 0px 12px rgba(56, 161, 105, 0.2)',
                            padding: '16px 24px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            width: '420px',
                        }}
                    >
                        <CheckCircleIcon sx={{ color: '#38a169' }} />

                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" sx={{ color: '#2f855a', fontWeight: 'bold' }}>
                                {isEdited ? "Program edited Successfully" : "Program Saved Successfully"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4a5568' }}>
                                {formData?.program} has been successfully {isEdited ? "edited" : "saved"} in the dashboard.
                            </Typography>
                        </Box>

                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Snackbar>
                <SuccessConfirmationDialog open={openDialogSuccess} onClose={() => { setOpenDialogSuccess(false); handleCancel() }} />
                <ErrorDialog open={openErrorDialog} onClose={() => { setOpenErrorDialog(false); setErrorMessage('') }} errorMessage={errorMessage} />
            </PageHeader>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="inherit"
                variant="fullWidth"
                TabIndicatorProps={{ style: { backgroundColor: '#009BD8', height: '3px' } }}
                sx={{
                    px: 4,
                    mt: '16px',
                    '& .MuiTabs-flexContainer': {
                        width: '100%',
                        'overflow-y': 'hidden',
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: 'white !important',
                        height: '4px !important'
                    },
                    '& .MuiTab-root': {
                        ...theme.typography.tabLabel,
                        color: '#F5F5F5 !important',
                        textTransform: 'none',
                        minHeight: '50px',
                        height: '50px',
                        padding: '12px 0px',
                        flex: '1 1 20%',
                        width: '20% !important',
                        maxWidth: '20% !important',
                        backgroundColor: '#003771 !important',
                        opacity: '1 !important'
                    },
                    '& .MuiTab-root.Mui-selected': {
                        color: ' #009BD8 !important',
                        backgroundColor: '#ffffff !important',
                        borderBottom: '9px solid #009BD8 !important'
                    }
                }}
            >
                <Tab label="Program" />
                <Tab label="Projects" disabled={!formData?.id || (formData?.program.length > 64)} />
                <Tab label="Jobs/Pipelines" disabled={!formData?.id || (formData?.program.length > 64)} />
                <Tab label="SLAs" disabled={!formData?.id || (formData?.program.length > 64)} />

                <Tab label="Alerts" />
            </Tabs>


            {/* Form Section-fU */}
            {activeTab === 0 && (
                <ProgramTab sourceData={formData} setSourceData={setFormData} programOptions={programOptions} sectorOptions={sectorOptions} />
            )}
            {activeTab === 1 && (
                <ProjectTab open={openModal.isOpen} handleEditClose={handleEditClose}
                    programData={openModal}
                    programID={programID}
                    programName={programName}
                    setProjectData={setProjectData}
                />
            )}
            {activeTab === 2 && (
                <JobsPipelinesTab projectData={projectData} />
            )}
            {activeTab === 3 && (
                <SLATab handleEditClose={handleEditClose}
                    programData={openModal}
                    programID={programID}
                    programName={programName}
                    setProjectData={setProjectData} />
            )}
            {activeTab === 4 && (
                <AlertsTab
                    sourceData={formData}
                    setSourceData={setFormData}
                    programOptions={programOptions}
                    sectorOptions={sectorOptions}
                    projectData={projectData} // --- Pass projectData
                    programName={programName} // --- Pass programName
                    programID={programID} // --- Pass programID

                    // --- Props for state preservation ---
                    showAddForm={showAddForm}
                    setShowAddForm={setShowAddForm}
                    newAlertData={newAlertData}
                    setNewAlertData={setNewAlertData}
                    editingAlertId={editingAlertId}
                    setEditingAlertId={setEditingAlertId}
                    initialAlertData={initialAlertData} // Pass the initial data for resets
                    defaultBodyText={defaultBodyText} // Pass the default text
                />
            )}
            
        </PageWrapper>
    );
};

export default ProgramTabDashboard;
